import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Card, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { KYCDocument } from '@pflegenavi/service-provider-frontend/api';
import {
  useCreateUBO,
  useCreateUBODeclaration,
  useDeleteUBO,
  useGetAllCountriesPhoenix,
  useGetKycDocuments,
  useGetServiceProviderPhoenix,
  useGetUBODeclarations,
  useSubmitUBODeclaration,
} from '@pflegenavi/service-provider-frontend/api';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { enqueueSnackbar } from 'notistack';
import { KycDocumentView } from './KycDocumentView';
import { UboDeclarationView } from './UboDeclarationView';
import { UploadDocumentsModal } from './UploadDocumentsModal';
import type { UboFormData } from './UboFormModal';
import { UboFormModal } from './UboFormModal';
import {
  DOCUMENT_TYPES_BY_PERSON_TYPE,
  resolveKycDocumentStatus,
} from './kycDocuments';

export const Kyc: FC = () => {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { data: kycData, isPending: isKycLoading } = useGetKycDocuments();
  const { data: serviceProviderData, isPending: isServiceProviderLoading } =
    useGetServiceProviderPhoenix();
  const { data: uboDeclarationsData, isPending: isUboLoading } =
    useGetUBODeclarations();
  const { mutateAsync: createUBODeclaration } = useCreateUBODeclaration();
  const { mutateAsync: createUBO } = useCreateUBO();
  const { mutateAsync: submitUBODeclaration } = useSubmitUBODeclaration();
  const { mutateAsync: deleteUBO } = useDeleteUBO();
  const { data: countries } = useGetAllCountriesPhoenix();

  const [selectedDocumentType, setSelectedDocumentType] = useState<
    KYCDocument['Type'] | null
  >(null);
  const [isUBOFormOpen, setIsUBOFormOpen] = useState(false);
  const legalPersonType =
    serviceProviderData?.data?.legal_person_type ?? 'SOLETRADER';
  const documentTypes = legalPersonType
    ? DOCUMENT_TYPES_BY_PERSON_TYPE[legalPersonType]
    : [];

  const latestDeclaration = uboDeclarationsData
    ? uboDeclarationsData.ubo_declarations
        .slice()
        .sort((a, b) => b.CreationDate - a.CreationDate)[0]
    : undefined;

  const handleCreateUBODeclaration = useCallback(async () => {
    try {
      await createUBODeclaration();
      enqueueSnackbar({
        variant: 'success',
        message: t('kyc.ubo.declaration-created'),
      });
    } catch (err) {
      enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
    }
  }, [createUBODeclaration, enqueueErrorSnackbar, t]);

  const handleAddUBO = useCallback(
    async (data: UboFormData) => {
      const nationality = countries?.find(
        (country) => country.id === data.Nationality
      );
      const birthplaceCountry = countries?.find(
        (country) => country.id === data.Birthplace.Country
      );
      const addressCountry = countries?.find(
        (country) => country.id === data.Address.Country
      );

      if (
        !latestDeclaration ||
        !data.Birthday ||
        !nationality?.isoCode ||
        !birthplaceCountry?.isoCode ||
        !addressCountry?.isoCode
      ) {
        return;
      }

      try {
        await createUBO({
          params: {
            ubo_declaration_id: latestDeclaration.Id,
          },
          body: {
            ubo: {
              ...data,
              Birthday: data.Birthday,
              Nationality: nationality.isoCode,
              Birthplace: {
                ...data.Birthplace,
                Country: birthplaceCountry.isoCode,
              },
              Address: {
                ...data.Address,
                Country: addressCountry.isoCode,
              },
            },
          },
        });
        setIsUBOFormOpen(false);
        enqueueSnackbar({
          variant: 'success',
          message: t('kyc.ubo.ubo-added'),
        });
      } catch (err) {
        enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
      }
    },
    [countries, createUBO, enqueueErrorSnackbar, latestDeclaration, t]
  );

  const handleSubmitDeclaration = useCallback(async () => {
    if (!latestDeclaration) {
      return;
    }

    try {
      await submitUBODeclaration({
        ubo_declaration_id: latestDeclaration.Id,
      });
      enqueueSnackbar({
        variant: 'success',
        message: t('kyc.ubo.declaration-submitted'),
      });
    } catch (err) {
      enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
    }
  }, [enqueueErrorSnackbar, latestDeclaration, submitUBODeclaration, t]);

  const handleDeleteUbo = useCallback(
    async (uboId: string) => {
      if (!latestDeclaration) {
        return;
      }

      try {
        await deleteUBO({
          ubo_declaration_id: latestDeclaration.Id,
          ubo_id: uboId,
        });
        enqueueSnackbar({
          variant: 'success',
          message: t('kyc.ubo.ubo-deleted'),
        });
      } catch (err) {
        enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
      }
    },
    [latestDeclaration, deleteUBO, t, enqueueErrorSnackbar]
  );

  if (isKycLoading || isServiceProviderLoading || isUboLoading) {
    return null;
  }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Card sx={{ p: 1 }}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {documentTypes.map(({ type, name }) => (
            <KycDocumentView
              key={type}
              name={t(name)}
              isVerified={Boolean(kycData?.user?.KYCLevel === 'REGULAR')}
              // Same logic as on the mobile app.
              // If there is a document of the type that is currently validated, we show that one
              // If there isn't, but there is one that is waiting to be validated, we show that one
              // Otherwise, if there is a refused or out-of-date one, we show that one
              status={resolveKycDocumentStatus(kycData?.kyc_documents, type)}
              onEdit={() => setSelectedDocumentType(type)}
            />
          ))}
        </Stack>
      </Card>
      {legalPersonType === 'BUSINESS' && (
        <Card sx={{ p: 1 }}>
          <UboDeclarationView
            declaration={latestDeclaration}
            onCreateDeclaration={handleCreateUBODeclaration}
            onAddUbo={() => setIsUBOFormOpen(true)}
            onSubmitDeclaration={handleSubmitDeclaration}
            onDeleteUbo={handleDeleteUbo}
          />
        </Card>
      )}
      {selectedDocumentType && (
        <UploadDocumentsModal
          type={selectedDocumentType}
          legalUserType={legalPersonType}
          open={true}
          handleClose={() => setSelectedDocumentType(null)}
        />
      )}
      <UboFormModal
        open={isUBOFormOpen}
        handleClose={() => setIsUBOFormOpen(false)}
        onSubmit={handleAddUBO}
      />
    </Stack>
  );
};
